import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { MonetizationProvider } from './contexts/MonetizationContext';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ChatPage from './pages/ChatPage';
import StoryPage from './pages/StoryPage';
import StoryViewPage from './pages/StoryViewPage';
import HistoryPage from './pages/HistoryPage';
import ChildrenPage from './pages/ChildrenPage';
import SubscriptionPage from './pages/SubscriptionPage';
import AdminPage from './pages/AdminPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SupportPage from './pages/SupportPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import DeleteAccountPage from './pages/DeleteAccountPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import QuizPage from './pages/QuizPage';
import NewsletterQuizPage from './pages/NewsletterQuizPage';
import OuvrirPage from './pages/OuvrirPage';
import BadgesPage from './pages/BadgesPage';
import AdminStoryV2Page from './pages/AdminStoryV2Page';
import { initPostHog } from './services/analytics';

// Init PostHog analytics
initPostHog();
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #B8D4E3 0%, #A7D3D8 25%, #9ECDC4 50%, #A8D5BA 75%, #B5DCAE 100%)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg font-medium" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>Chargement...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #B8D4E3 0%, #A7D3D8 25%, #9ECDC4 50%, #A8D5BA 75%, #B5DCAE 100%)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg font-medium" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>Chargement...</p>
        </div>
      </div>
    );
  }

  // Permettre l'accès aux pages de reset de mot de passe même si connecté
  const currentPath = window.location.pathname;
  if (currentPath === '/reset-password' || currentPath === '/forgot-password') {
    return children;
  }

  return isAuthenticated ? <Navigate to="/chat" replace /> : children;
};

// Route pour la page d'accueil - Landing si non connecté, redirige vers chat si connecté
const HomeRoute = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #B8D4E3 0%, #A7D3D8 25%, #9ECDC4 50%, #A8D5BA 75%, #B5DCAE 100%)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg font-medium" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>Chargement...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? <Navigate to="/chat" replace /> : <LandingPage />;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Routes de reset de mot de passe - PRIORITAIRES */}
      <Route 
        path="/reset-password" 
        element={<ResetPasswordPage />}
      />
      <Route 
        path="/forgot-password" 
        element={<ForgotPasswordPage />}
      />
      
      {/* Routes publiques normales */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        } 
      />
      
      {/* Landing page publique */}
      <Route 
        path="/welcome" 
        element={<LandingPage />} 
      />
      
      {/* Pages statiques publiques */}
      <Route 
        path="/support" 
        element={<SupportPage />} 
      />
      <Route 
        path="/confidentialite" 
        element={<PrivacyPolicyPage />} 
      />
      <Route 
        path="/suppression-compte" 
        element={<DeleteAccountPage />} 
      />
      <Route 
        path="/conditions-utilisation" 
        element={<TermsOfServicePage />} 
      />
      
      {/* Routes protégées pour l'application */}
      <Route 
        path="/chat" 
        element={
          <ProtectedRoute>
            <ChatPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/story" 
        element={
          <ProtectedRoute>
            <StoryPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/story/view/:storyId?" 
        element={
          <ProtectedRoute>
            <StoryViewPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/history" 
        element={
          <ProtectedRoute>
            <HistoryPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/children" 
        element={
          <ProtectedRoute>
            <ChildrenPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/subscription" 
        element={
          <ProtectedRoute>
            <SubscriptionPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/quiz" 
        element={
          <ProtectedRoute>
            <QuizPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/badges/:childId" 
        element={
          <ProtectedRoute>
            <BadgesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/admin/story-v2" 
        element={
          <ProtectedRoute>
            <AdminStoryV2Page />
          </ProtectedRoute>
        } 
      />

      {/* Page de redirection universelle — ouvre l'app native ou fallback web */}
      <Route path="/ouvrir" element={<OuvrirPage />} />

      <Route 
        path="/quiz/newsletter/:newsletterId" 
        element={
          <ProtectedRoute>
            <NewsletterQuizPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Route principale - Landing si non connecté, redirige vers chat si connecté */}
      <Route 
        path="/" 
        element={
          <HomeRoute />
        } 
      />
      
      {/* Catch-all - redirige vers la page d'accueil */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  React.useEffect(() => {
    // Détection globale et agressive du token de reset
    const handleTokenDetection = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      const currentPath = window.location.pathname;
      
      console.log('🔍 Token detection global:', {
        url: window.location.href,
        path: currentPath,
        token: token,
        hasToken: !!token
      });

      // Si on a un token et qu'on n'est PAS sur reset-password, forcer la redirection
      if (token && currentPath !== '/reset-password') {
        console.log('🚨 Token détecté sur route non-reset, redirection forcée!');
        
        // Redirection forcée avec window.location pour éviter React Router
        window.location.href = `/reset-password?token=${token}`;
        return;
      }
    };

    // Appeler immédiatement
    handleTokenDetection();
    
    // Appeler aussi au changement d'URL
    window.addEventListener('popstate', handleTokenDetection);
    
    return () => {
      window.removeEventListener('popstate', handleTokenDetection);
    };
  }, []);

  return (
    <AuthProvider>
      <MonetizationProvider>
        <BrowserRouter>
          <div className="App">
            <AppRoutes />
          </div>
        </BrowserRouter>
      </MonetizationProvider>
    </AuthProvider>
  );
}

export default App;